import React, { useState } from 'react';
import {
  Box,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  Button,
  useColorModeValue,
} from '@chakra-ui/react';

const getMeta = (metaData, key) => metaData?.find(m => m.key === key)?.value || '';

// Descarta valores placeholder de perfiles incompletos (ej. "0", "a") que
// aparecen en datos reales cuando el campo nunca se llenó de verdad — sin
// esto se ofrecían como opción nombres como "0" o "a a".
const isRealName = (name) => {
  const trimmed = name.trim();
  return trimmed.length >= 2 && !/^[0]+$/.test(trimmed) && trimmed.toLowerCase() !== 'a';
};

// Ninguno de los 3 (apoderado/padre/madre) es un usuario de datofy-api (ni
// tiene id ahí) — sus datos viven como meta_data del alumno en WooCommerce.
// El shape resultante ({ name, username, first_name, last_name, email })
// coincide a propósito con el que ya arma StaffPicker, para que
// buildCustomer()/submitSurveyResponse (ScannerShell.js) y el guardado de
// texto plano en student_statuses.responsible_name funcionen sin cambios —
// Respondent::firstOrCreate() en el backend solo necesita un `username`
// (ver SurveyController::responseSurveyFromWebhook), no un usuario real.
const buildApoderadoPerson = (metaData) => {
  const name = getMeta(metaData, 'apoderado_nombre');
  if (!name || !isRealName(name)) return null;
  const rut = getMeta(metaData, 'apoderado_rut');
  const [first, ...rest] = name.split(' ');
  return {
    name,
    username: rut || `apoderado-${name}`,
    first_name: first || name,
    last_name: rest.join(' '),
    email: getMeta(metaData, 'apoderado_email') || null,
    role: 'Apoderado',
    source: 'apoderado',
  };
};

// "padre"/"madre" — datos separados en nombres_X/apellido_paterno_X/
// apellido_materno_X (sin RUT ni email propios en el meta_data del alumno,
// a diferencia del apoderado financiero).
const buildFamilyPerson = (metaData, which, role) => {
  const nombres = getMeta(metaData, `nombres_${which}`);
  const apellidoPaterno = getMeta(metaData, `apellido_paterno_${which}`);
  const apellidoMaterno = getMeta(metaData, `apellido_materno_${which}`);
  const name = [nombres, apellidoPaterno, apellidoMaterno].filter(Boolean).join(' ').trim();
  if (!name || !isRealName(nombres)) return null;
  return {
    name,
    username: `${which}-${name.toLowerCase().replace(/\s+/g, '-')}`,
    first_name: nombres,
    last_name: [apellidoPaterno, apellidoMaterno].filter(Boolean).join(' '),
    email: null,
    role,
    source: which,
  };
};

const buildOtroPerson = (typedName) => {
  const name = typedName.trim();
  if (!name) return null;
  return {
    name,
    username: `otro-${name.toLowerCase().replace(/\s+/g, '-')}`,
    first_name: name,
    last_name: '',
    email: null,
    role: 'Otra persona',
    source: 'otro',
  };
};

// Selector de "quien retira/acompaña" para Salida por Enfermedad — a
// diferencia de StaffPicker (busca personal real de datofy-api), acá se
// ofrecen los contactos YA REGISTRADOS en el perfil del alumno (apoderado
// financiero, madre, padre — pueden ser hasta 3), más una opción de texto
// libre para cuando acompaña alguien que no está en ninguna base de datos.
//
// `variant`: 'cards' (default, usado por StudentPicker/SickExitsTab/etc. —
// no tocar ese look) o 'dropdown' — mismas opciones (apoderado/madre/padre),
// pero como <Select> nativo en vez de tarjetas, para el flujo de Salida por
// Enfermedad del Scanner (ScannerShell > StudentInfoCard), donde el
// operador necesita elegir rápido de un vistazo.
const GuardianPicker = ({ metaData, value, onSelect, placeholder = 'Nombre de quien retira…', variant = 'cards' }) => {
  const [otherName, setOtherName] = useState('');
  const options = [
    buildApoderadoPerson(metaData),
    buildFamilyPerson(metaData, 'madre', 'Madre'),
    buildFamilyPerson(metaData, 'padre', 'Padre'),
  ].filter(Boolean);
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  if (value) {
    return (
      <HStack p={2} borderRadius="8px" border="1px solid" borderColor={borderColor} justify="space-between">
        <VStack align="flex-start" spacing={0} minW={0}>
          <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{value.name}</Text>
          <Text fontSize="xs" color={mutedColor}>
            {value.role || 'Otra persona'}
            {value.source === 'apoderado' && value.username ? ` · RUT ${value.username}` : ''}
          </Text>
        </VStack>
        <Text fontSize="xs" color="primary.500" cursor="pointer" fontWeight="semibold" onClick={() => onSelect(null)}>
          Cambiar
        </Text>
      </HStack>
    );
  }

  if (variant === 'dropdown') {
    return (
      <VStack spacing={2} align="stretch">
        {options.length > 0 ? (
          <Select
            placeholder="Selecciona responsable…"
            size="md" h="44px" fontSize="14.5px" borderRadius="9px"
            border="1.5px solid" borderColor="cream.line2"
            _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}
            onChange={(e) => {
              const opt = options.find(o => o.username === e.target.value);
              if (opt) onSelect(opt);
            }}
          >
            {options.map(opt => (
              <option key={opt.source} value={opt.username}>
                {opt.name} · {opt.role}{opt.source === 'apoderado' && opt.username ? ` · RUT ${opt.username}` : ''}
              </option>
            ))}
          </Select>
        ) : (
          <Text fontSize="xs" color={mutedColor}>El alumno no tiene apoderado, madre ni padre registrados en su perfil.</Text>
        )}
        <HStack>
          <Input
            size="md" h="40px" fontSize="13.5px"
            borderRadius="8px"
            placeholder={placeholder}
            value={otherName}
            onChange={(e) => setOtherName(e.target.value)}
          />
          <Button
            size="md" h="40px"
            isDisabled={!otherName.trim()}
            onClick={() => {
              const person = buildOtroPerson(otherName);
              if (person) onSelect(person);
            }}
          >
            Usar
          </Button>
        </HStack>
      </VStack>
    );
  }

  return (
    <VStack spacing={2} align="stretch">
      {options.length > 0 ? (
        options.map(opt => (
          <Box
            key={opt.source}
            as="button"
            type="button"
            onClick={() => onSelect(opt)}
            p={2}
            borderRadius="8px"
            border="1px solid"
            borderColor={borderColor}
            textAlign="left"
            _hover={{ borderColor: 'primary.400' }}
          >
            <Text fontSize="sm" fontWeight="semibold">{opt.name}</Text>
            <Text fontSize="xs" color={mutedColor}>
              {opt.role}{opt.source === 'apoderado' && opt.username ? ` · RUT ${opt.username}` : ''}
            </Text>
          </Box>
        ))
      ) : (
        <Text fontSize="xs" color={mutedColor}>El alumno no tiene apoderado, madre ni padre registrados en su perfil.</Text>
      )}
      <HStack>
        <Input
          size="sm"
          borderRadius="8px"
          placeholder={placeholder}
          value={otherName}
          onChange={(e) => setOtherName(e.target.value)}
        />
        <Button
          size="sm"
          isDisabled={!otherName.trim()}
          onClick={() => {
            const person = buildOtroPerson(otherName);
            if (person) onSelect(person);
          }}
        >
          Usar
        </Button>
      </HStack>
    </VStack>
  );
};

export default GuardianPicker;
